import { z } from 'zod';
import type {
  CreateGoodsReceiptBody,
  CreatePurchaseOrderBody,
  CreateSupplierInvoiceBody,
  GoodsReceiptLine,
  PurchaseOrder,
  UpdatePurchaseOrderBody,
} from '../api';
import { add, compare, divide, isDecimal, multiply, normalize } from './decimal';
import { receiptValue, toBase, type Discount, type TotalsLine } from './totals';

/** The API's decimal input format: up to 15 integer digits and 4 decimals. */
export const DECIMAL_RE = /^\d{1,15}(\.\d{1,4})?$/;
export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const decimalText = (label: string) =>
  z
    .string()
    .trim()
    .refine((v) => v === '' || DECIMAL_RE.test(v), {
      message: `${label}: enter a number with at most 4 decimals`,
    });

const positive = (label: string) =>
  z
    .string()
    .trim()
    .refine((v) => DECIMAL_RE.test(v), {
      message: `Enter a number with at most 4 decimals`,
    })
    .refine((v) => !DECIMAL_RE.test(v) || compare(v, '0') > 0, {
      message: `${label} must be more than 0`,
    });

const discountFields = {
  discountType: z.enum(['AMOUNT', 'PERCENT']),
  discountValue: decimalText('Discount'),
};

function percentOk(v: { discountType: 'AMOUNT' | 'PERCENT'; discountValue: string }) {
  return (
    v.discountType !== 'PERCENT' || v.discountValue === '' || compare(v.discountValue, '100') <= 0
  );
}

/** The picked item, as much as the form needs of a `/variants` row. */
export interface PickedVariant {
  id: string;
  productId?: string;
  sku: string;
  displayName: string;
  purchasePrice?: string | null;
  baseUnit?: { id: string; symbol: string; allowsFraction: boolean };
}

// --- purchase order --------------------------------------------------------------------------

export const orderLineSchema = z
  .object({
    key: z.string(),
    variant: z
      .custom<PickedVariant | null>()
      .refine((v): boolean => !!v, { message: 'Choose an item' }),
    unitId: z.string(),
    unitSymbol: z.string(),
    factor: z.string(),
    description: z.string().trim().max(400),
    qty: positive('Quantity'),
    unitPrice: decimalText('Price'),
    taxRateId: z.string(),
    ...discountFields,
  })
  .refine(percentOk, { message: 'At most 100 %', path: ['discountValue'] });

export const orderFormSchema = z
  .object({
    supplier: z
      .custom<{ id: string; name: string } | null>()
      .refine((v): boolean => !!v, { message: 'Choose a supplier' }),
    docDate: z.string().regex(DATE_RE, { message: 'Pick a date' }),
    expectedDate: z.string(),
    locationId: z.string(),
    reference: z.string().trim().max(100),
    notes: z.string().trim().max(2000),
    ...discountFields,
    lines: z.array(orderLineSchema).min(1, { message: 'Add at least one item' }).max(500),
  })
  .refine(percentOk, { message: 'At most 100 %', path: ['discountValue'] })
  .refine((v) => !v.expectedDate || v.expectedDate >= v.docDate, {
    message: 'Expected on or after the order date',
    path: ['expectedDate'],
  });

export type OrderForm = z.infer<typeof orderFormSchema>;
export type OrderLineForm = OrderForm['lines'][number];

let seq = 0;
export function lineKey(): string {
  seq += 1;
  return `l${String(seq)}${Math.random().toString(36).slice(2, 7)}`;
}

export function emptyOrderLine(): OrderLineForm {
  return {
    key: lineKey(),
    variant: null,
    unitId: '',
    unitSymbol: '',
    factor: '1',
    description: '',
    qty: '1',
    unitPrice: '',
    taxRateId: '',
    discountType: 'PERCENT',
    discountValue: '',
  };
}

export function emptyOrderForm(today: string): OrderForm {
  return {
    supplier: null,
    docDate: today,
    expectedDate: '',
    locationId: '',
    reference: '',
    notes: '',
    discountType: 'AMOUNT',
    discountValue: '',
    lines: [emptyOrderLine()],
  };
}

/** Default cost per entered unit: the item's last purchase cost × unit factor. */
export function defaultUnitPrice(purchasePrice: string | null | undefined, factor: string): string {
  if (!purchasePrice || !isDecimal(purchasePrice)) return '';
  return normalize(multiply(purchasePrice, factor, 4));
}

/** Rescales a typed price when the unit changes (box of 4 → pc: ÷ 4). */
export function rescalePrice(price: string, fromFactor: string, toFactor: string): string {
  if (!price || !DECIMAL_RE.test(price) || fromFactor === toFactor) return price;
  const perBase = Number(price) / Number(fromFactor);
  if (!Number.isFinite(perBase)) return price;
  return normalize(multiply(perBase.toFixed(10), toFactor, 4));
}

export function toDiscount(type: 'AMOUNT' | 'PERCENT', value: string): Discount | null {
  const v = value.trim();
  if (!v || !DECIMAL_RE.test(v) || compare(v, '0') === 0) return null;
  return { type, value: normalize(v) };
}

/** Lines → the totals engine input (tax rates looked up by id). */
export function orderTotalsInput(
  lines: OrderLineForm[],
  rateOf: (taxRateId: string) => string | undefined,
): TotalsLine[] {
  return lines.map((l) => ({
    qty: DECIMAL_RE.test(l.qty) ? l.qty : '0',
    unitPrice: DECIMAL_RE.test(l.unitPrice) ? l.unitPrice : '0',
    discount: toDiscount(l.discountType, l.discountValue),
    taxRate: l.taxRateId ? (rateOf(l.taxRateId) ?? '0') : '0',
  }));
}

function orderLinesBody(form: OrderForm, taxEnabled: boolean) {
  return form.lines.map((l) => {
    const discount = toDiscount(l.discountType, l.discountValue);
    return {
      variantId: l.variant?.id ?? '',
      ...(l.unitId ? { unitId: l.unitId } : {}),
      qty: normalize(l.qty),
      ...(l.unitPrice ? { unitPrice: normalize(l.unitPrice) } : {}),
      ...(discount ? { discount } : {}),
      ...(taxEnabled ? { taxRateId: l.taxRateId || null } : {}),
      ...(l.description.trim() ? { description: l.description.trim() } : {}),
    };
  });
}

export function toCreateOrderBody(
  form: OrderForm,
  opts: { taxEnabled: boolean; confirm: boolean },
): CreatePurchaseOrderBody {
  const discount = toDiscount(form.discountType, form.discountValue);
  return {
    contactId: form.supplier?.id ?? '',
    docDate: form.docDate,
    ...(form.expectedDate ? { expectedDate: form.expectedDate } : {}),
    ...(form.locationId ? { locationId: form.locationId } : {}),
    ...(discount ? { discount } : {}),
    ...(form.reference.trim() ? { reference: form.reference.trim() } : {}),
    ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    lines: orderLinesBody(form, opts.taxEnabled),
    confirm: opts.confirm,
  };
}

export function toUpdateOrderBody(
  form: OrderForm,
  opts: { taxEnabled: boolean; version: number },
): UpdatePurchaseOrderBody {
  return {
    contactId: form.supplier?.id ?? '',
    docDate: form.docDate,
    expectedDate: form.expectedDate || null,
    ...(form.locationId ? { locationId: form.locationId } : {}),
    discount: toDiscount(form.discountType, form.discountValue),
    reference: form.reference.trim() || null,
    notes: form.notes.trim() || null,
    lines: orderLinesBody(form, opts.taxEnabled),
    version: opts.version,
  };
}

/** A saved draft back into the form (variants are rebuilt from the line snapshot). */
export function orderToForm(po: PurchaseOrder): OrderForm {
  return {
    supplier: { id: po.contact.id, name: po.contact.name },
    docDate: po.docDate.slice(0, 10),
    expectedDate: po.expectedDate?.slice(0, 10) ?? '',
    locationId: po.location.id,
    reference: po.reference ?? '',
    notes: po.notes ?? '',
    discountType: po.discount?.type ?? 'AMOUNT',
    discountValue: po.discount ? normalize(po.discount.value) : '',
    lines: po.lines.map((l) => ({
      key: lineKey(),
      variant: { id: l.variantId, sku: l.sku, displayName: l.description },
      unitId: l.unitId,
      unitSymbol: l.unitSymbol,
      factor: normalize(l.factor),
      description: l.description,
      qty: normalize(l.qty),
      unitPrice: normalize(l.unitPrice),
      taxRateId: l.taxRateId ?? '',
      discountType: l.discount?.type ?? 'PERCENT',
      discountValue: l.discount ? normalize(l.discount.value) : '',
    })),
  };
}

// --- goods receipt ---------------------------------------------------------------------------

/** Receive-now rows for every order line with something still to come (default 0). */
export function initialRows(po: PurchaseOrder): ReceiveRow[] {
  return po.lines
    .filter((l) => compare(l.qtyToReceive, '0') > 0)
    .map((l) => ({
      purchaseOrderLineId: l.id,
      outstanding: normalize(l.qtyToReceive),
      qty: '',
      rejected: '',
      reason: '',
    }));
}

export interface ReceiveRow {
  purchaseOrderLineId: string;
  /** Still to receive, in the line unit. */
  outstanding: string;
  qty: string;
  rejected: string;
  reason: string;
}

export interface RowErrors {
  qty?: string;
  rejected?: string;
  reason?: string;
}

/** Validates receive-now rows; returns errors by line id (empty = valid). */
export function validateReceiveRows(rows: ReceiveRow[]): Record<string, RowErrors> {
  const out: Record<string, RowErrors> = {};
  for (const r of rows) {
    const e: RowErrors = {};
    const qty = r.qty.trim() || '0';
    const rejected = r.rejected.trim() || '0';
    if (!DECIMAL_RE.test(qty)) e.qty = 'Enter a number';
    else if (compare(qty, r.outstanding) > 0) e.qty = `Only ${normalize(r.outstanding)} left`;
    if (!DECIMAL_RE.test(rejected)) e.rejected = 'Enter a number';
    else if (compare(rejected, '0') > 0 && r.reason.trim().length < 3)
      e.reason = 'Say why (3+ characters)';
    if (Object.keys(e).length) out[r.purchaseOrderLineId] = e;
  }
  return out;
}

/** Lines with something received; an empty result means nothing to post. */
export function receiveLines(rows: ReceiveRow[]) {
  return rows
    .filter((r) => compare(r.qty || '0', '0') > 0 || compare(r.rejected || '0', '0') > 0)
    .map((r) => ({
      purchaseOrderLineId: r.purchaseOrderLineId,
      qty: normalize(r.qty.trim() || '0'),
      ...(compare(r.rejected || '0', '0') > 0
        ? { rejectedQty: normalize(r.rejected.trim()), rejectReason: r.reason.trim() }
        : {}),
    }));
}

export function toReceiptBody(input: {
  purchaseOrderId: string;
  docDate: string;
  supplierRef: string;
  notes: string;
  rows: ReceiveRow[];
}): CreateGoodsReceiptBody {
  return {
    purchaseOrderId: input.purchaseOrderId,
    docDate: input.docDate,
    ...(input.supplierRef.trim() ? { supplierRef: input.supplierRef.trim() } : {}),
    ...(input.notes.trim() ? { notes: input.notes.trim() } : {}),
    lines: receiveLines(input.rows),
  };
}

/** Direct receipt (no order): each line carries its own item, unit and cost. */
export const directLineSchema = z
  .object({
    key: z.string(),
    variant: z
      .custom<PickedVariant | null>()
      .refine((v): boolean => !!v, { message: 'Choose an item' }),
    unitId: z.string(),
    unitSymbol: z.string(),
    factor: z.string(),
    qty: decimalText('Quantity'),
    rejected: decimalText('Rejected'),
    reason: z.string().trim().max(500),
    unitPrice: positiveOrZero('Cost'),
    taxRateId: z.string(),
  })
  .refine((l) => compare(l.qty || '0', '0') > 0 || compare(l.rejected || '0', '0') > 0, {
    message: 'Enter a quantity',
    path: ['qty'],
  })
  .refine((l) => compare(l.rejected || '0', '0') === 0 || l.reason.length >= 3, {
    message: 'Say why (3+ characters)',
    path: ['reason'],
  });

function positiveOrZero(label: string) {
  return z
    .string()
    .trim()
    .refine((v) => DECIMAL_RE.test(v), { message: `${label}: enter a number` });
}

export const directReceiptSchema = z.object({
  supplier: z
    .custom<{ id: string; name: string } | null>()
    .refine((v): boolean => !!v, { message: 'Choose a supplier' }),
  locationId: z.string(),
  docDate: z.string().regex(DATE_RE, { message: 'Pick a date' }),
  supplierRef: z.string().trim().max(100),
  notes: z.string().trim().max(2000),
  lines: z.array(directLineSchema).min(1, { message: 'Add at least one item' }).max(500),
});

export type DirectReceiptForm = z.infer<typeof directReceiptSchema>;
export type DirectLineForm = DirectReceiptForm['lines'][number];

export function emptyDirectLine(): DirectLineForm {
  return {
    key: lineKey(),
    variant: null,
    unitId: '',
    unitSymbol: '',
    factor: '1',
    qty: '1',
    rejected: '',
    reason: '',
    unitPrice: '',
    taxRateId: '',
  };
}

/** Stock value of a direct receipt before tax: Σ round(qty × cost) at the currency's decimals. */
export function directReceiptValue(
  lines: { qty: string; unitPrice: string }[],
  decimals: number,
): string {
  let sum = '0';
  for (const l of lines) {
    if (!DECIMAL_RE.test(l.qty || '') || !DECIMAL_RE.test(l.unitPrice || '')) continue;
    sum = add(sum, multiply(l.qty, l.unitPrice, decimals));
  }
  return multiply(sum, '1', decimals);
}

export function toDirectReceiptBody(
  form: DirectReceiptForm,
  taxEnabled: boolean,
): CreateGoodsReceiptBody {
  return {
    contactId: form.supplier?.id ?? '',
    ...(form.locationId ? { locationId: form.locationId } : {}),
    docDate: form.docDate,
    ...(form.supplierRef.trim() ? { supplierRef: form.supplierRef.trim() } : {}),
    ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    lines: form.lines.map((l) => ({
      variantId: l.variant?.id ?? '',
      ...(l.unitId ? { unitId: l.unitId } : {}),
      qty: normalize(l.qty || '0'),
      unitPrice: normalize(l.unitPrice),
      ...(compare(l.rejected || '0', '0') > 0
        ? { rejectedQty: normalize(l.rejected), rejectReason: l.reason.trim() }
        : {}),
      ...(taxEnabled && l.taxRateId ? { taxRateId: l.taxRateId } : {}),
    })),
  };
}

// --- supplier bill ---------------------------------------------------------------------------

/** A receipt line offered for billing. `defaultPrice` is its receipt value per unit. */
export interface BillReceiptRow {
  goodsReceiptLineId: string;
  include: boolean;
  qty: string;
  /** Unbilled quantity in the line unit. */
  maxQty: string;
  unitPrice: string;
  defaultPrice: string;
  /** '' = no tax, 'default' = keep the order/receipt rate, else a tax rate id. */
  taxRateId: string;
  discountType: 'AMOUNT' | 'PERCENT';
  discountValue: string;
}

export interface BillExpenseRow {
  key: string;
  accountId: string;
  description: string;
  qty: string;
  unitPrice: string;
  taxRateId: string;
  discountType: 'AMOUNT' | 'PERCENT';
  discountValue: string;
}

export interface BillHeader {
  contactId: string;
  supplierInvoiceNumber: string;
  docDate: string;
  dueDate: string;
  reference: string;
  notes: string;
  discountType: 'AMOUNT' | 'PERCENT';
  discountValue: string;
}

export function emptyExpenseRow(): BillExpenseRow {
  return {
    key: lineKey(),
    accountId: '',
    description: '',
    qty: '1',
    unitPrice: '',
    taxRateId: '',
    discountType: 'PERCENT',
    discountValue: '',
  };
}

/** A receipt line's starting bill row: everything unbilled, at its receipt value, order tax. */
export function billReceiptRow(line: GoodsReceiptLine): BillReceiptRow {
  const max = normalize(line.qtyToBill);
  const price = compare(line.qty, '0') > 0 ? normalize(divide(line.totalCost, line.qty, 4)) : '0';
  return {
    goodsReceiptLineId: line.id,
    include: true,
    qty: max,
    maxQty: max,
    unitPrice: price,
    defaultPrice: price,
    taxRateId: 'default',
    discountType: 'PERCENT',
    discountValue: '',
  };
}

/**
 * Bill rows → totals engine input, in the order the server sees them (included receipt rows, then
 * expenses). An unchanged receipt price is valued exactly like the server does: the receipt value
 * spread by cumulative quantity, so partial bills add up to the receipt cost.
 */
export function billTotalsInput(
  receipts: BillReceiptRow[],
  expenses: BillExpenseRow[],
  opts: {
    source: (goodsReceiptLineId: string) => GoodsReceiptLine | undefined;
    rateOf: (taxRateId: string) => string | undefined;
    /** The order line's rate for "As ordered". */
    defaultRateOf: (goodsReceiptLineId: string) => string | undefined;
    decimals: number;
  },
): TotalsLine[] {
  const num = (v: string) => (DECIMAL_RE.test(v.trim()) ? v.trim() : '0');
  const out: TotalsLine[] = [];
  for (const r of receipts) {
    if (!r.include) continue;
    const line = opts.source(r.goodsReceiptLineId);
    const qty = num(r.qty);
    const unchanged = !r.unitPrice.trim() || compare(num(r.unitPrice), r.defaultPrice) === 0;
    const taxRate =
      r.taxRateId === 'default'
        ? (opts.defaultRateOf(r.goodsReceiptLineId) ?? '0')
        : r.taxRateId
          ? (opts.rateOf(r.taxRateId) ?? '0')
          : '0';
    out.push({
      qty,
      unitPrice: unchanged ? r.defaultPrice : num(r.unitPrice),
      ...(unchanged && line
        ? {
            gross: receiptValue({
              totalCost: line.totalCost,
              qtyAccepted: line.qtyAccepted,
              qtyBefore: add(line.qtyBilled, line.qtyReturned),
              qtyBase: toBase(qty, line.factor),
              decimals: opts.decimals,
            }),
          }
        : {}),
      discount: toDiscount(r.discountType, r.discountValue),
      taxRate,
    });
  }
  for (const x of expenses)
    out.push({
      qty: num(x.qty),
      unitPrice: num(x.unitPrice),
      discount: toDiscount(x.discountType, x.discountValue),
      taxRate: x.taxRateId ? (opts.rateOf(x.taxRateId) ?? '0') : '0',
    });
  return out;
}

export type BillErrors = Record<string, string>;

/** Validates the bill; keys are `header.<field>`, `r.<lineId>.<field>`, `e.<key>.<field>`. */
export function validateBill(
  header: BillHeader,
  receipts: BillReceiptRow[],
  expenses: BillExpenseRow[],
): BillErrors {
  const e: BillErrors = {};
  if (!header.contactId) e['header.contactId'] = 'Choose a supplier';
  if (!DATE_RE.test(header.docDate)) e['header.docDate'] = 'Pick a date';
  if (header.dueDate && header.dueDate < header.docDate)
    e['header.dueDate'] = 'Due on or after the bill date';
  if (header.discountValue && !DECIMAL_RE.test(header.discountValue))
    e['header.discountValue'] = 'Enter a number';
  else if (header.discountType === 'PERCENT' && header.discountValue)
    if (compare(header.discountValue, '100') > 0) e['header.discountValue'] = 'At most 100 %';
  const included = receipts.filter((r) => r.include);
  for (const r of included) {
    const k = `r.${r.goodsReceiptLineId}`;
    if (!DECIMAL_RE.test(r.qty) || compare(r.qty, '0') <= 0) e[`${k}.qty`] = 'More than 0';
    else if (compare(r.qty, r.maxQty) > 0) e[`${k}.qty`] = `Only ${normalize(r.maxQty)} to bill`;
    if (r.unitPrice && !DECIMAL_RE.test(r.unitPrice)) e[`${k}.unitPrice`] = 'Enter a number';
    if (r.discountValue && !DECIMAL_RE.test(r.discountValue))
      e[`${k}.discountValue`] = 'Enter a number';
  }
  for (const x of expenses) {
    const k = `e.${x.key}`;
    if (!x.accountId) e[`${k}.accountId`] = 'Choose an account';
    if (!x.description.trim()) e[`${k}.description`] = 'Describe it';
    if (!DECIMAL_RE.test(x.qty) || compare(x.qty, '0') <= 0) e[`${k}.qty`] = 'More than 0';
    if (!DECIMAL_RE.test(x.unitPrice)) e[`${k}.unitPrice`] = 'Enter the amount';
    if (x.discountValue && !DECIMAL_RE.test(x.discountValue))
      e[`${k}.discountValue`] = 'Enter a number';
  }
  if (included.length + expenses.length === 0) e.lines = 'Add at least one line to bill';
  return e;
}

export function toBillBody(
  header: BillHeader,
  receipts: BillReceiptRow[],
  expenses: BillExpenseRow[],
  opts: { taxEnabled: boolean },
): CreateSupplierInvoiceBody {
  const discount = toDiscount(header.discountType, header.discountValue);
  const tax = (id: string) =>
    !opts.taxEnabled || id === 'default' ? {} : { taxRateId: id || null };
  return {
    contactId: header.contactId,
    ...(header.supplierInvoiceNumber.trim()
      ? { supplierInvoiceNumber: header.supplierInvoiceNumber.trim() }
      : {}),
    docDate: header.docDate,
    ...(header.dueDate ? { dueDate: header.dueDate } : {}),
    ...(discount ? { discount } : {}),
    ...(header.reference.trim() ? { reference: header.reference.trim() } : {}),
    ...(header.notes.trim() ? { notes: header.notes.trim() } : {}),
    lines: [
      ...receipts
        .filter((r) => r.include)
        .map((r) => {
          const d = toDiscount(r.discountType, r.discountValue);
          const priced = r.unitPrice && compare(r.unitPrice, r.defaultPrice) !== 0;
          return {
            goodsReceiptLineId: r.goodsReceiptLineId,
            qty: normalize(r.qty),
            // Unchanged price → the server bills the exact receipt value (no price variance).
            ...(priced ? { unitPrice: normalize(r.unitPrice) } : {}),
            ...(d ? { discount: d } : {}),
            ...tax(r.taxRateId),
          };
        }),
      ...expenses.map((x) => {
        const d = toDiscount(x.discountType, x.discountValue);
        return {
          accountId: x.accountId,
          description: x.description.trim(),
          qty: normalize(x.qty),
          unitPrice: normalize(x.unitPrice),
          ...(d ? { discount: d } : {}),
          ...(opts.taxEnabled && x.taxRateId ? { taxRateId: x.taxRateId } : {}),
        };
      }),
    ],
  };
}
